import { useNavigate, useParams } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import {
  Filter,
  Grid,
  List,
  X,
  FolderOpen,
  ArrowRight,
  Lock,
  Sparkles,
  Network,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getCatalogTree,
  createScenarioRun,
  createBuildYourOwnRun,
} from "../api/services/hierarchyServices";
import { createMultiVmLab } from "../api/services/labService";
import { useAuth } from "../context/AuthContext";
import Layout from "../layouts/Layout";
import CompanyUserLayout from "../layouts/CompanyUserLayout";
import CompanyAdminLayout from "../layouts/CompanyAdminLayout";
import AdminLayout from "../layouts/AdminLayout";
import NodeCard from "../components/NodeCard";
import NodeCardCyberDecks from "../components/NodeCardCyberDecks";
import Breadcrumbs from "../components/Breadcrumbs";
import EmergingThreatRequestModal from "../components/EmergingThreatRequestModal";
import MultiVmLabRequestModal from "../components/MultiVmLabRequestModal";
import { useToast } from "../components/ToastContext";
import ThreeNodeNetwork from "../components/ThreeNodeNetwork";
import BuildYourOwnScenarioModal from "../components/BuildYourOwnScenarioModal";

function findNodeById(node, id) {
  if (node.id === id || node.slug === id) return node;

  for (const child of node.children || []) {
    const found = findNodeById(child, id);
    if (found) return found;
  }

  return null;
}

function findPath(node, id, path = []) {
  if (!node) return null;
  if (node.id === id || node.slug === id) return [...path, node];

  for (const child of node.children || []) {
    const found = findPath(child, id, [...path, node]);
    if (found) return found;
  }

  return null;
}

function findNearestCategoryId(path = [], fallbackNode = null) {
  for (let index = path.length - 1; index >= 0; index -= 1) {
    if (path[index]?.type === "category" && path[index]?.id) {
      return path[index].id;
    }
  }

  if (fallbackNode?.type === "category" && fallbackNode?.id) {
    return fallbackNode.id;
  }

  return null;
}
// Grid view skeleton — mirrors the image-cover card (NodeCardCyberDecks, h-[280px])
function SkeletonGridCard() {
  return (
    <div className="qv-card qv-noise relative h-[280px] rounded-2xl overflow-hidden border border-(--color-border) bg-(--color-bg-overlay) dark:bg-white/5 animate-pulse">
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      {/* Top bar: icon + badge */}
      <div className="absolute inset-x-0 top-0 flex items-center justify-between p-5">
        <div className="h-9 w-9 rounded-xl bg-(--color-primary)/10" />
        <div className="h-5 w-16 rounded-md bg-(--color-bg-card) dark:bg-white/10" />
      </div>
      {/* Bottom bar: title + hover cue */}
      <div className="absolute inset-x-0 bottom-0 p-5 space-y-2">
        <div className="h-5 w-3/5 rounded bg-(--color-bg-card) dark:bg-white/15" />
        <div className="h-3 w-1/3 rounded bg-(--color-bg-card) dark:bg-white/10" />
      </div>
    </div>
  );
}

// List view skeleton — mirrors the horizontal strip card (NodeCard)
function SkeletonListCard() {
  return (
    <div className="qv-card qv-noise flex min-h-[140px] items-center gap-4 rounded-2xl border border-(--color-border) bg-(--color-bg-overlay) dark:bg-white/5 p-6 animate-pulse">
      <div className="h-10 w-10 shrink-0 rounded-xl bg-(--color-primary)/10" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-2/5 rounded bg-(--color-bg-card) dark:bg-white/15" />
        <div className="h-3 w-1/4 rounded bg-(--color-bg-card) dark:bg-white/10" />
      </div>
      <div className="hidden sm:block h-6 w-20 rounded-full bg-(--color-bg-card) dark:bg-white/10" />
      <div className="h-5 w-5 rounded bg-(--color-bg-card) dark:bg-white/10" />
    </div>
  );
}

// Network view skeleton — scattered node placeholders for the 3D graph
function SkeletonNetwork() {
  const nodes = [
    { top: "18%", left: "22%", size: "h-16 w-16" },
    { top: "30%", left: "60%", size: "h-12 w-12" },
    { top: "55%", left: "38%", size: "h-20 w-20" },
    { top: "62%", left: "72%", size: "h-14 w-14" },
    { top: "44%", left: "12%", size: "h-10 w-10" },
    { top: "72%", left: "18%", size: "h-12 w-12" },
    { top: "20%", left: "82%", size: "h-10 w-10" },
  ];
  return (
    <div className="relative h-[60vh] w-full rounded-2xl border border-(--color-border) bg-(--color-bg-overlay) dark:bg-white/5 overflow-hidden animate-pulse">
      {nodes.map((node, index) => (
        <div
          key={index}
          className={`absolute rounded-full bg-(--color-primary)/15 ${node.size}`}
          style={{ top: node.top, left: node.left }}
        />
      ))}
    </div>
  );
}

export default function BoxesPage() {
  const { nodeId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [treeData, setTreeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCustomRequestModal, setShowCustomRequestModal] = useState(false);
  const [customRequestCategoryId, setCustomRequestCategoryId] = useState(null);
  // Which custom-request modal the open request targets. The page-level
  // `isBuildYourOwn` is derived from the URL path, but the network view drops
  // the nodeId (navigates to /boxes) so it can't be relied on there — the
  // request handlers set this explicitly based on the focused node's intent.
  const [customRequestIsBuildYourOwn, setCustomRequestIsBuildYourOwn] =
    useState(false);

  // Open the custom-request modal for a specific category; falls back to
  // requestCategoryId (current page) when none is passed. `buildYourOwn`
  // selects which modal renders, defaulting to the URL-derived page flag.
  const handleOpenCustomRequest = (categoryId, buildYourOwn = isBuildYourOwn) => {
    setCustomRequestCategoryId(categoryId || requestCategoryId);
    setCustomRequestIsBuildYourOwn(buildYourOwn);
    setShowCustomRequestModal(true);
  };

  // Entry point from the 3D network view's request button: route to the
  // teammate's existing modals — Multi-VM Labs -> MultiVmLabRequestModal,
  // Build Your Own -> BuildYourOwnScenarioModal, everything else ->
  // EmergingThreatRequestModal for the focused category.
  const handleRequestFromNetwork = (categoryId, isMultiVm, isBuildYourOwnNode) => {
    if (isMultiVm) {
      setShowMultiVmModal(true);
    } else {
      handleOpenCustomRequest(categoryId, isBuildYourOwnNode);
    }
  };
  const hasFilters = search.trim().length > 0 || selectedDifficulty;
  const { showToast } = useToast();

  const isRtl = i18n.language === "ar";
  const roles = user?.roles || [];
  const isCompanyUser = roles.includes("company_user");
  const isCompanyAdmin = roles.includes("company_admin");
  const isPlatformAdmin = roles.includes("platform_admin");
  const canRequestScenario = Boolean(user?.permissions?.scenario_creation);
  const teamId = user?.team_ids?.[0] ?? user?.team_id ?? null;

  useEffect(() => {
    let isActive = true;

    const loadTree = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const data = await getCatalogTree();
        if (isActive) {
          setTreeData(data);
        }
      } catch (error) {
        if (isActive) {
          setLoadError(error?.message || "Failed to load catalog tree.");
          setTreeData(null);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadTree();
    return () => {
      isActive = false;
    };
  }, [refreshKey]);

  const currentNode = treeData
    ? nodeId
      ? findNodeById(treeData, nodeId)
      : treeData
    : null;
  const safeNode = currentNode || treeData;
  const currentPath = useMemo(() => {
    if (!treeData || !safeNode?.id) {
      return [];
    }
    return findPath(treeData, safeNode.id) || [];
  }, [treeData, safeNode]);
  const isEmergingThreatCustomVersions = useMemo(() => {
    const normalizedTitles = currentPath.map((item) =>
      String(item?.title || "")
        .trim()
        .toLowerCase(),
    );
    const lastTitle = normalizedTitles[normalizedTitles.length - 1] || "";

    return (
      normalizedTitles.some((title) => title.includes("emerging threats")) &&
      lastTitle.includes("custom versions")
    );
  }, [currentPath]);
  const isBuildYourOwn = useMemo(() => {
    const lastTitle = String(currentPath[currentPath.length - 1]?.title || "")
      .trim()
      .toLowerCase();
    return lastTitle.includes("build your own");
  }, [currentPath]);
  const showCustomRequestCTA = isEmergingThreatCustomVersions || isBuildYourOwn;
  const isMultiVmCategory = useMemo(() => {
    const lastTitle = String(currentPath[currentPath.length - 1]?.title || "")
      .trim()
      .toLowerCase();
    return lastTitle.includes("multi-vm");
  }, [currentPath]);
  const [multiVmCve, setMultiVmCve] = useState("");
  const [multiVmTheme, setMultiVmTheme] = useState("");
  const [isSubmittingMultiVm, setIsSubmittingMultiVm] = useState(false);
  const [showMultiVmModal, setShowMultiVmModal] = useState(false);
  const requestCategoryId = useMemo(
    () => findNearestCategoryId(currentPath, safeNode),
    [currentPath, safeNode],
  );

  const difficultyFilters = [
    { label: "Easy", color: "success" },
    { label: "Medium", color: "warning" },
    { label: "Hard", color: "danger" },
    { label: "Insane", color: "info" },
  ];

  const children = safeNode?.children || [];

  const counts = useMemo(() => {
    const titleIncludes = (title, keyword) =>
      title.toLowerCase().includes(keyword.toLowerCase());

    const difficultyCount = (label) =>
      children.filter(
        (c) =>
          (c.difficulty &&
            c.difficulty.toLowerCase() === label.toLowerCase()) ||
          titleIncludes(c.title || "", label),
      ).length;

    return {
      difficulties: Object.fromEntries(
        difficultyFilters.map((d) => [d.label, difficultyCount(d.label)]),
      ),
    };
  }, [children]);

  const filteredChildren = useMemo(() => {
    const q = search.trim().toLowerCase();
    return children.filter((c) => {
      const title = (c.title || "").toLowerCase();
      const matchesSearch = q ? title.includes(q) : true;
      const matchesDifficulty = selectedDifficulty
        ? c.difficulty
          ? c.difficulty.toLowerCase() === selectedDifficulty.toLowerCase()
          : title.includes(selectedDifficulty.toLowerCase())
        : true;
      return matchesSearch && matchesDifficulty;
    });
  }, [children, search, selectedDifficulty]);
  const handleSubmitBuildYourOwn = async (requestData) => {
    // Prefer the explicitly-requested category (set by the network view, which
    // drops the URL nodeId) and fall back to the current page's category.
    const categoryId = customRequestCategoryId || requestCategoryId;
    if (!categoryId) {
      throw new Error("Category ID is missing for this request.");
    }

    const requestPayload = {
      category_id: categoryId,
      team_id: teamId,
      domain: requestData.domain || "",
      strategy: requestData.strategy || "",
      cve: requestData.cve || null,
      vuln_theme: requestData.vuln_theme || "",
      ttp: requestData.ttp || {},
      noise: Boolean(requestData.noise),
      controls: requestData.controls || {},
      difficulty: requestData.difficulty || "",
      duration: requestData.duration || "",
      framework: requestData.framework || "",
      ...(requestData.topology_id
        ? { topology_id: requestData.topology_id }
        : {}),
    };

    console.log("[Build Your Own] sent to backend:", requestPayload);

    try {
      const response = await createBuildYourOwnRun(requestPayload);
      console.log("[Build Your Own] response:", response);

      showToast("Scenario request submitted successfully", "success");
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("[Build Your Own] Failed:", error);
      showToast(
        error?.message ||
          "Failed to submit scenario request. Please try again.",
        "error",
      );
      throw error;
    }
  };

  const handleSubmitCustomRequest = async (requestData) => {
    const categoryId = customRequestCategoryId || requestCategoryId;
    if (!categoryId) {
      throw new Error("Category ID is missing for this request.");
    }

    const requestPayload = {
      category_id: categoryId,
      cve: requestData.cveTitle,
      vuln_theme: requestData.description,
      framework: requestData.framework,
      team_id: teamId,
      ...(requestData.topology_id
        ? { topology_id: requestData.topology_id }
        : {}),
    };

    console.log("[Emerging Threat] sent to backend:", requestPayload);

    try {
      const response = await createScenarioRun(requestPayload);
      console.log("[Emerging Threat] response:", response);

      showToast("Box request submitted successfully", "success");
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error("[Emerging Threat] Failed:", error);
      const errorMessage =
        error?.message || "Failed to submit box request. Please try again.";
      showToast(errorMessage, "error");
      throw error;
    }
  };

  const handleSubmitMultiVmLab = async () => {
    if (!multiVmTheme.trim()) {
      showToast("Please describe the vulnerability theme.", "warning");
      return;
    }
    try {
      setIsSubmittingMultiVm(true);
      const response = await createMultiVmLab({
        cve: multiVmCve.trim() || null,
        vulnTheme: multiVmTheme.trim(),
      });
      showToast("Multi-VM lab request submitted!", "success");
      setMultiVmCve("");
      setMultiVmTheme("");
      setShowMultiVmModal(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      showToast(error?.message || "Failed to submit lab request.", "error");
    } finally {
      setIsSubmittingMultiVm(false);
    }
  };

  const renderEmptyState = () => (
    <div className="w-full h-80 col-span-full p-10 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-md border border-(--color-primary)/30 bg-(--color-primary)/10">
        <FolderOpen className="h-6 w-6 text-(--color-primary)" />
      </div>
      <h3 className="mt-2 text-lg font-semibold text-(--color-text-primary)">
        {loadError ? "Catalog unavailable" : "Coming Soon"}
      </h3>
      <p className="mt-2 text-sm text-(--color-text-muted) dark:text-(--color-text-muted-dark)">
        {loadError
          ? loadError
          : hasFilters
            ? "No challenges match your current filters. Try clearing them to see more."
            : "This category is empty right now. Check back later for new challenges."}
      </p>
      {loadError ? (
        <button
          type="button"
          className="mt-6 inline-flex items-center justify-center rounded-lg border border-(--color-primary)/40 bg-(--color-primary)/10 px-4 py-2 text-sm font-semibold text-(--color-primary) transition hover:bg-(--color-primary)/20"
          onClick={() => setRefreshKey((prev) => prev + 1)}
        >
          Retry
        </button>
      ) : hasFilters ? (
        <button
          type="button"
          className="cursor-pointer mt-6 inline-flex items-center justify-center rounded-lg border border-(--color-primary)/40 bg-(--color-primary)/10 px-4 py-2 text-sm font-semibold text-(--color-primary) transition hover:bg-(--color-primary)/20"
          onClick={() => {
            setSearch("");
            setSelectedDifficulty(null);
          }}
        >
          Clear filters
        </button>
      ) : null}
    </div>
  );

  const PageLayout = isCompanyUser
    ? CompanyUserLayout
    : isCompanyAdmin
      ? CompanyAdminLayout
      : isPlatformAdmin
        ? AdminLayout
        : Layout;

  return (
    <PageLayout>
      {customRequestIsBuildYourOwn ? (
        <BuildYourOwnScenarioModal
          open={showCustomRequestModal}
          onClose={() => setShowCustomRequestModal(false)}
          onSubmit={handleSubmitBuildYourOwn} // ← was handleSubmitCustomRequest
        />
      ) : (
        <EmergingThreatRequestModal
          open={showCustomRequestModal}
          onClose={() => setShowCustomRequestModal(false)}
          onSubmit={handleSubmitCustomRequest}
        />
      )}
      <MultiVmLabRequestModal
        open={showMultiVmModal}
        onClose={() => setShowMultiVmModal(false)}
        cve={multiVmCve}
        setCve={setMultiVmCve}
        theme={multiVmTheme}
        setTheme={setMultiVmTheme}
        isSubmitting={isSubmittingMultiVm}
        onSubmit={handleSubmitMultiVmLab}
      />
      <div className="w-full max-w-362.5 mx-auto px-8 py-8 space-y-10 min-h-screen">
        <div className="space-y-4">
          <Breadcrumbs currentNode={safeNode} tree={treeData} />
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-(--color-text-primary)">
                  Practice Challenges
                </h1>
                <p className="text-xs mt-0.5 text-(--color-text-muted)">
                  Structured challenges to advance your cybersecurity skills
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className={`cursor-pointer p-2 rounded-lg border transition-colors ${
                    view === "network"
                      ? "bg-(--color-primary)/10 border-(--color-primary) text-(--color-primary)"
                      : "bg-(--color-bg-card) dark:bg-black border-(--color-border) text-(--color-text-muted) hover:bg-(--color-primary)/10"
                  }`}
                  onClick={() => {
                    // Tree view always starts from the catalog root, so drop any
                    // drilled-in nodeId to reset the breadcrumb path.
                    if (nodeId) {
                      navigate("/boxes");
                    }
                    setView("network");
                  }}
                  aria-pressed={view === "network"}
                  title="3D Network View"
                >
                  <Network className="h-5 w-5" />
                </button>
                <button
                  className={`cursor-pointer p-2 rounded-lg border transition-colors ${
                    view === "grid"
                      ? "bg-(--color-primary)/10 border-(--color-primary) text-(--color-primary)"
                      : "bg-(--color-bg-card) dark:bg-black border-(--color-border) text-(--color-text-muted) hover:bg-(--color-primary)/10"
                  }`}
                  onClick={() => setView("grid")}
                  aria-pressed={view === "grid"}
                  title="Grid View"
                >
                  <Grid className="h-5 w-5" />
                </button>
                <button
                  className={`cursor-pointer p-2 rounded-lg border transition-colors ${
                    view === "list"
                      ? "bg-(--color-primary)/10 border-(--color-primary) text-(--color-primary)"
                      : "bg-(--color-bg-card) dark:bg-black border-(--color-border) text-(--color-text-muted) hover:bg-white/5 dark:hover:bg-white/10"
                  }`}
                  onClick={() => setView("list")}
                  aria-pressed={view === "list"}
                  title="List View"
                >
                  <List className="h-5 w-5" />
                </button>
                <div className="relative">
                  <button
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-(--color-bg-card) dark:bg-black border border-(--color-border) text-(--color-text-primary) hover:bg-white/5 dark:hover:bg-white/10 transition-colors"
                    onClick={() => setShowFilters((prev) => !prev)}
                  >
                    <Filter className={`h-4 w-4 ${isRtl ? "ml-1" : "mr-1"}`} />
                    Filters
                  </button>
                  {showFilters && (
                    <div
                      className={`absolute top-full mt-2 w-80 z-20 space-y-5 rounded-sm border border-(--color-primary)/20 bg-(--color-bg-card) backdrop-blur-sm p-5 shadow-[0_0_25px_rgba(0,0,0,0.45)] ${
                        isRtl ? "left-0" : "right-0"
                      }`}
                      dir={isRtl ? "rtl" : "ltr"}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-(--color-primary)/90">
                          Filters
                        </p>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            className="cursor-pointer text-xs text-(--color-text-muted) hover:text-(--color-primary) transition-colors"
                            onClick={() => {
                              setSearch("");
                              setSelectedDifficulty(null);
                            }}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            className="cursor-pointer p-1 text-(--color-text-muted) hover:text-(--color-primary) transition-colors"
                            onClick={() => setShowFilters(false)}
                            aria-label="Close filters"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <input
                          type="text"
                          placeholder="Search items..."
                          className="w-full px-4 py-2.5 rounded-lg bg-(--color-bg-card) dark:bg-black border border-(--color-border) text-(--color-text-primary) placeholder-(--color-text-muted) focus:outline-none focus:border-(--color-primary) focus:ring-2 focus:ring-(--color-primary)/30 focus:shadow-[0_0_12px_rgba(0,255,0,0.35)] transition"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>

                      <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-(--color-text-primary)">
                          Difficulty
                        </h3>
                        <div className="flex flex-col gap-2">
                          {difficultyFilters.map((f) => (
                            <button
                              key={f.label}
                              className={`flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all text-(--color-text-primary) ${
                                selectedDifficulty === f.label
                                  ? "bg-(--color-primary)/10 border-(--color-primary) shadow-[0_0_12px_rgba(0,255,0,0.25)]"
                                  : "bg-(--color-bg-card) dark:bg-black border-(--color-border) hover:border-(--color-primary)/60 hover:-translate-y-px"
                              }`}
                              onClick={() => setSelectedDifficulty(f.label)}
                            >
                              <span className="flex items-center gap-2 font-medium">
                                <span
                                  className={`w-2 h-2 rounded-full ${
                                    f.color === "success"
                                      ? "bg-(--color-primary)"
                                      : f.color === "warning"
                                        ? "bg-yellow-400"
                                        : f.color === "danger"
                                          ? "bg-red-500"
                                          : "bg-blue-400"
                                  }`}
                                ></span>
                                {f.label}
                              </span>
                              <span className="text-xs px-2 py-1 rounded-full bg-(--color-bg-overlay) dark:bg-white/5 text-(--color-text-muted) dark:text-gray-300 font-semibold">
                                {counts.difficulties[f.label] || 0}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {showCustomRequestCTA && !loadError ? (
              isCompanyUser && !canRequestScenario ? (
                <div className="group relative overflow-hidden bg-transparent border border-(--color-primary)/20 px-8 py-7 mt-2 rounded-2xl cursor-not-allowed">
                  {/* ── Decorative glows (hidden on hover) ── */}
                  <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-(--color-primary)/10 blur-3xl transition-opacity duration-300 group-hover:opacity-0" />
                  <div className="pointer-events-none absolute -left-10 bottom-0 h-32 w-32 rounded-full bg-(--color-primary)/5 blur-2xl transition-opacity duration-300 group-hover:opacity-0" />

                  {/* ── Left curtain ── */}
                  <div className="pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-(--color-primary) -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out z-10 rounded-l-2xl" />
                  {/* ── Right curtain ── */}
                  <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-(--color-primary) translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out z-10 rounded-r-2xl" />

                  {/* ── Overlay message (appears after curtains meet) ── */}
                  <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-300 text-white dark:text-black">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 border border-white/30 dark:bg-black/10 dark:border-black/20">
                      <Lock className="h-5 w-5 text-white dark:text-black" />
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-base font-bold">Permission Required</p>
                      <p className="text-sm text-white/85 dark:text-black/80 max-w-sm">
                        You don't have permission to request custom versions.
                        Contact your administrator to get access.
                      </p>
                    </div>
                  </div>

                  {/* ── Normal content (fades out on hover) ── */}
                  <div className="relative transition-opacity duration-200 group-hover:opacity-0 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between bg-transparent">
                    <div className="max-w-2xl space-y-2">
                      <div className="inline-flex items-center gap-2 rounded-md border border-(--color-primary)/30 bg-transparent px-3 py-1 text-xs font-semibold uppercase tracking-widest text-(--color-primary)">
                        Custom Box
                      </div>
                      <h2 className="text-2xl font-bold text-(--color-text-primary) [text-shadow:0_0_16px_var(--color-shadow-primary)]">
                        Build a version tailored to your objectives
                      </h2>
                      <p className="max-w-xl text-sm leading-relaxed text-(--color-text-muted) dark:text-gray-400">
                        Open a guided request form to capture the CVE title, box
                        description, preferred saved topology, and framework for
                        this custom version.
                      </p>
                    </div>

                    <div className="flex flex-col items-start gap-2 shrink-0">
                      <button
                        type="button"
                        disabled
                        className="inline-flex cursor-not-allowed items-center justify-center gap-2 rounded-xl border border-(--color-border) bg-transparent dark:bg-transparent px-6 py-3 text-sm font-semibold text-(--color-text-muted) select-none"
                      >
                        <Lock className="h-4 w-4" />
                        Request Custom Version
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative overflow-hidden bg-transparent border border-(--color-primary)/20 px-8 py-7 mt-2 rounded-md">
                  <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-md bg-transparent" />
                  <div className="pointer-events-none absolute bottom-0 left-1/3 h-px w-1/3 bg-transparent" />
                  <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="max-w-2xl space-y-2">
                      <div className="inline-flex items-center gap-2 rounded-md border border-(--color-primary)/30 bg-transparent px-3 py-1 text-xs font-semibold uppercase tracking-widest text-(--color-primary)">
                        Custom Box
                      </div>
                      <h2 className="text-2xl font-bold text-(--color-text-primary) [text-shadow:0_0_16px_var(--color-shadow-primary)]">
                        Build a version tailored to your objectives
                      </h2>
                      <p className="max-w-xl text-sm leading-relaxed text-(--color-text-muted) dark:text-gray-400">
                        Open a guided request form to capture the CVE title, box
                        description, preferred saved topology, and framework for
                        this custom version.
                      </p>
                    </div>

                    <div className="flex flex-col items-start gap-2 shrink-0">
                      <button
                        type="button"
                        className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-(--color-primary) bg-(--color-primary) px-6 py-3 text-sm font-semibold text-(--color-text-on-primary) shadow-[0_0_20px_rgba(0,200,80,0.25)] transition hover:-translate-y-px hover:shadow-[0_0_28px_rgba(0,200,80,0.4)] hover:opacity-95"
                        onClick={() => handleOpenCustomRequest(requestCategoryId)}
                      >
                        Request Custom Version
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            ) : null}

            {/* Multi-VM Lab Request Banner */}
            {isMultiVmCategory && !loadError && (
              <div className="relative overflow-hidden bg-transparent border border-(--color-primary)/20 px-8 py-7 mt-2 rounded-md">
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-md bg-(--color-primary)/10 blur-3xl" />
                <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-2xl space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="inline-flex items-center gap-2 rounded-md border border-(--color-primary)/30 bg-transparent px-3 py-1 text-xs font-semibold uppercase tracking-widest text-(--color-primary)">
                        Multi-VM Lab
                      </div>
                      <span className="inline-flex items-center gap-1 rounded-full border border-violet-400/50 bg-violet-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-300">
                        <Sparkles className="h-2.5 w-2.5" />
                        Beta
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-(--color-text-primary) [text-shadow:0_0_16px_var(--color-shadow-primary)]">
                      Request a Multi-VM Lab
                    </h2>
                    <p className="max-w-xl text-sm leading-relaxed text-(--color-text-muted) dark:text-gray-400">
                      Describe a vulnerability theme and optionally provide a
                      CVE. The AI engine will build a multi-VM lab with network
                      isolation and pivot scenarios.
                    </p>
                  </div>

                  <div className="flex flex-col items-start gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowMultiVmModal(true)}
                      className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-(--color-primary) bg-(--color-primary) px-6 py-3 text-sm font-semibold text-(--color-text-on-primary) shadow-[0_0_20px_rgba(0,200,80,0.25)] transition hover:-translate-y-px hover:shadow-[0_0_28px_rgba(0,200,80,0.4)] hover:opacity-95"
                    >
                      Request Lab
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-6 mt-2">
          {/* Content */}
          <div className="flex-1 min-h-[45vh]">
            {isLoading ? (
              view === "network" ? (
                <SkeletonNetwork />
              ) : view === "grid" ? (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonGridCard key={index} />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <SkeletonListCard key={index} />
                  ))}
                </div>
              )
            ) : view === "network" ? (
              treeData ? (
                <ThreeNodeNetwork
                  treeData={treeData}
                  onRequestCustom={handleRequestFromNetwork}
                />
              ) : (
                renderEmptyState()
              )
            ) : view === "grid" ? (
              filteredChildren.length ? (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredChildren.map((child) => (
                    <NodeCardCyberDecks
                      key={child.id || child.flow_run_id}
                      node={child}
                      // MULTIVM-DUMMY-TOPOLOGY: enables placeholder topology button on Multi-VM labs
                      inMultiVmCategory={isMultiVmCategory}
                    />
                  ))}
                </div>
              ) : (
                renderEmptyState()
              )
            ) : (
              <div className="space-y-3">
                {filteredChildren.length
                  ? filteredChildren.map((child) => (
                      <div
                        key={child.id || child.flow_run_id}
                        className="rounded-2xl"
                      >
                        {/* MULTIVM-DUMMY-TOPOLOGY: enables placeholder topology button on Multi-VM labs */}
                        <NodeCard node={child} inMultiVmCategory={isMultiVmCategory} />
                      </div>
                    ))
                  : renderEmptyState()}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 text-(--color-text-muted) dark:text-gray-400 text-sm">
          <span></span>
        </div>
      </div>
    </PageLayout>
  );
}
